import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import { downloadFromBase64 } from '@/shared/utils/downloadFile'

/**
 * Descarga el PDF de una factura. Usa el query del módulo portal del gateway
 * (`portalExportBillingToPdf`), que valida con el rol del portal y enruta a Zeus
 * vía portal-server. Zeus genera el PDF al vuelo y lo devuelve en base64 (ya no
 * se persiste en MinIO); armamos el archivo y lo descargamos. Se ejecuta
 * on-click (lazy), no en render.
 */
const EXPORT_BILLING_PDF = gql`
  query PortalExportBillingToPdf($billingId: Int!, $simplified: Boolean) {
    portalExportBillingToPdf(billingId: $billingId, simplified: $simplified) {
      success
      fileBase64
      fileName
      error
    }
  }
`

interface ExportPdfResult {
  portalExportBillingToPdf: {
    success: boolean
    fileBase64?: string | null
    fileName?: string | null
    error?: string | null
  }
}

export function useExportBillingPdf() {
  const [run, { loading }] = useLazyQuery<ExportPdfResult>(EXPORT_BILLING_PDF, {
    fetchPolicy: 'network-only',
  })

  const download = async (id: number, invoiceNumber?: string) => {
    try {
      const result = await run({ variables: { billingId: id } })
      const res = result.data?.portalExportBillingToPdf
      if (res?.success && res.fileBase64) {
        downloadFromBase64(
          res.fileBase64,
          res.fileName || (invoiceNumber ? `factura-${invoiceNumber}.pdf` : `factura-${id}.pdf`),
        )
      } else {
        toast.error('No se pudo generar el PDF', {
          description: res?.error ?? result.error?.message ?? 'Inténtalo de nuevo más tarde.',
        })
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { operation: 'ExportBillingToPdf' } })
      toast.error('No se pudo descargar el PDF', {
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return { download, loading }
}
